import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/api';
import {
  getAnalyticsEventMeta,
  sortAnalyticsEventEntries,
  TAG_LABELS,
} from '../../constants/analyticsEventMeta';
import DailyBarChart from '../common/DailyBarChart';

// Event Type Card Component
const EventTypeCard = ({ title, count, percentage, tagLabel, description, onClick }) => (
  <div
    className="event-card cursor-pointer p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 border border-gray-200"
    onClick={onClick}
  >
    <div className="flex items-start justify-between gap-2 mb-1">
      <h3 className="event-card-title text-sm font-medium text-gray-600 truncate flex-1 min-w-0" title={title}>
        {title}
      </h3>
      <div
        className="relative shrink-0 group/info"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Event description"
          title={description || 'No description'}
          className="flex h-5 w-5 items-center justify-center rounded-full border border-gray-300 text-[10px] font-semibold leading-none text-gray-500 hover:border-gray-400 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
        >
          i
        </button>
        {description ? (
          <div
            role="tooltip"
            className="pointer-events-none absolute right-0 top-full z-20 mt-1 hidden w-56 rounded-md bg-gray-900 px-2.5 py-2 text-left text-xs font-normal leading-snug text-white shadow-lg group-hover/info:block group-focus-within/info:block"
          >
            {description}
          </div>
        ) : null}
      </div>
    </div>
    {tagLabel ? (
      <span className="mb-2 inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
        {tagLabel}
      </span>
    ) : null}
    <div className="event-card-count text-2xl font-bold text-blue-600 mb-1">{count}</div>
    <div className="event-card-percentage text-sm text-gray-500">{percentage}%</div>
  </div>
);

// Event Table Component
const EventTable = ({ events, onEventClick, clinicName }) => (
  <div className="event-table-container overflow-x-auto">
    <table className="min-w-full bg-white border border-gray-200 rounded-lg">
      <thead className="bg-gray-50">
        <tr>
          <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event Name</th>
          <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Session ID</th>
          <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {events.map(event => (
          <tr
            key={event.id}
            onClick={() => onEventClick(event)}
            className="event-row hover:bg-gray-50 cursor-pointer"
          >
            <td className="py-3 px-4 text-sm text-gray-900">{event.event_name}</td>
            <td className="py-3 px-4 text-sm text-gray-500 font-mono">{event.session_id.substring(0, 8)}...</td>
            <td className="py-3 px-4 text-sm text-gray-500">{new Date(event.timestamp).toLocaleString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Event Detail Modal Component
const EventDetailView = ({ event, onClose }) => {
  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return value.toString();
  };

  const renderEventDataFields = (data) => {
    if (!data || Object.keys(data).length === 0) {
      return <p className="text-gray-500">No event data available</p>;
    }

    return (
      <div className="event-data-fields space-y-3">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="event-data-field">
            <div className="event-data-label font-medium text-gray-700">{key}:</div>
            <div className="event-data-value text-sm text-gray-600 mt-1">
              {typeof value === 'object' ? (
                <pre className="bg-gray-50 p-2 rounded text-xs overflow-auto">{JSON.stringify(value, null, 2)}</pre>
              ) : (
                formatValue(value)
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="event-detail-header flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Event Details</h2>
          <button
            className="event-detail-close text-gray-400 hover:text-gray-600 text-2xl font-bold"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="event-detail-body p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="event-detail-section mb-6">
            <h3 className="text-lg font-medium text-gray-700 mb-4">Basic Information</h3>
            <div className="event-detail-info grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="event-info-item">
                <div className="event-info-label text-sm font-medium text-gray-500">Event ID:</div>
                <div className="event-info-value text-sm text-gray-900 font-mono">{event.id}</div>
              </div>
              <div className="event-info-item">
                <div className="event-info-label text-sm font-medium text-gray-500">Event Name:</div>
                <div className="event-info-value text-sm text-gray-900">{event.event_name}</div>
              </div>
              <div className="event-info-item">
                <div className="event-info-label text-sm font-medium text-gray-500">Session ID:</div>
                <div className="event-info-value text-sm text-gray-900 font-mono">{event.session_id}</div>
              </div>
              <div className="event-info-item">
                <div className="event-info-label text-sm font-medium text-gray-500">Reference ID:</div>
                <div className="event-info-value text-sm text-gray-900">{event.reference_id || '-'}</div>
              </div>
              <div className="event-info-item md:col-span-2">
                <div className="event-info-label text-sm font-medium text-gray-500">Timestamp:</div>
                <div className="event-info-value text-sm text-gray-900">{new Date(event.timestamp).toLocaleString()}</div>
              </div>
            </div>
          </div>

          <div className="event-detail-section mb-6">
            <h3 className="text-lg font-medium text-gray-700 mb-4">Event Data</h3>
            {renderEventDataFields(event.event_data)}
          </div>

          {event.metadata && Object.keys(event.metadata).length > 0 && (
            <div className="event-detail-section">
              <h3 className="text-lg font-medium text-gray-700 mb-4">Metadata</h3>
              {renderEventDataFields(event.metadata)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main ClinicAnalyticsSection Component
const ClinicAnalyticsSection = ({ clinicId, clinicName }) => {
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [summary, setSummary] = useState(null);
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [eventTypeData, setEventTypeData] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [error, setError] = useState(null);

  // Fetch analytics data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await analyticsService.getEventsSummary(days, clinicId);
        setSummary(data);
      } catch (error) {
        console.error('Failed to fetch analytics summary:', error);
        setError('Failed to load analytics data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [days, clinicId]);

  // Fetch event type data when selected
  useEffect(() => {
    const fetchEventTypeData = async () => {
      if (!selectedEventType) return;

      try {
        setLoading(true);
        const data = await analyticsService.getEventsByType(selectedEventType, clinicId);
        setEventTypeData(data);
      } catch (error) {
        console.error(`Failed to fetch events for type ${selectedEventType}:`, error);
        setError(`Failed to load events for ${selectedEventType}. Please try again later.`);
      } finally {
        setLoading(false);
      }
    };

    fetchEventTypeData();
  }, [selectedEventType, clinicId]);

  const handleEventCardClick = (eventType) => {
    setSelectedEventType(eventType);
    setSelectedEvent(null);
  };

  const handleBackClick = () => {
    setSelectedEventType(null);
    setEventTypeData(null);
    setSelectedEvent(null);
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Loading analytics data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">No analytics data available for this clinic.</p>
      </div>
    );
  }

  if (selectedEvent) {
    return (
      <div className="clinic-analytics">
        {selectedEvent && <EventDetailView event={selectedEvent} onClose={() => setSelectedEvent(null)} />}
      </div>
    );
  }

  if (selectedEventType && eventTypeData) {
    return (
      <div className="clinic-analytics">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 flex items-center"
                onClick={handleBackClick}
              >
                ← Back to Analytics
              </button>
              <h2 className="text-xl font-semibold text-gray-800">Events: {selectedEventType}</h2>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md mb-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Current Event Records (max 100)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Total Events</p>
                <p className="text-2xl font-bold text-blue-600">{eventTypeData.events.length}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Event Type</p>
                <p className="text-lg font-semibold text-gray-800">{selectedEventType}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Clinic</p>
                <p className="text-lg font-semibold text-gray-800">{clinicName}</p>
              </div>
            </div>
          </div>

          <EventTable
            events={eventTypeData.events}
            onEventClick={handleEventClick}
            clinicName={clinicName}
          />
        </div>
      </div>
    );
  }

  const totalEvents = summary.total_events || 0;
  const eventCards = sortAnalyticsEventEntries(Object.entries(summary.event_counts || {})).map(([name, count]) => {
    const meta = getAnalyticsEventMeta(name);
    const tag = meta.tags?.[0];
    const tagLabel = tag ? (TAG_LABELS[tag] ?? tag) : null;
    const percentage = totalEvents > 0 ? Math.round((count / totalEvents) * 100) : 0;
    return (
      <EventTypeCard
        key={name}
        title={name}
        count={count}
        percentage={percentage}
        tagLabel={tagLabel}
        description={meta.description}
        onClick={() => handleEventCardClick(name)}
      />
    );
  });

  return (
    <div className="clinic-analytics space-y-6">
      {/* Header with time period selector */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Clinic Analytics</h2>
          <p className="text-sm text-gray-600">Analytics data for {clinicName}</p>
        </div>
        <div className="flex items-center space-x-2">
          <label htmlFor="days-select" className="text-sm font-medium text-gray-700">Time Period:</label>
          <select
            id="days-select"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="1">Last 24 hours</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-medium text-gray-700 mb-2">Total Events</h3>
          <div className="text-3xl font-bold text-blue-600">{totalEvents.toLocaleString()}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-medium text-gray-700 mb-2">Event Types</h3>
          <div className="text-3xl font-bold text-green-600">{Object.keys(summary.event_counts || {}).length}</div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-medium text-gray-700 mb-2">Recent Events</h3>
          <div className="text-3xl font-bold text-purple-600">{summary.recent_events?.length || 0}</div>
        </div>
      </div>

      {/* Daily Chart */}
      {summary.daily_counts && summary.daily_counts.length > 0 && (
        <DailyBarChart
          title="Daily Event Counts"
          data={summary.daily_counts.map((item) => ({
            date: item.date,
            value: item.count,
          }))}
        />
      )}

      {/* Events by Type */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Events by Type</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {eventCards.length > 0 ? eventCards : <p className="text-gray-500">No events found for the selected period.</p>}
        </div>
      </div>

      {/* Recent Events */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Recent Events</h3>
        <EventTable
          events={summary.recent_events || []}
          onEventClick={handleEventClick}
          clinicName={clinicName}
        />
      </div>
    </div>
  );
};

export default ClinicAnalyticsSection;
